import React, { useCallback, useState } from 'react';
import { apiPost } from '../api';
import { track } from '../lib/analytics';

// Renders a button that opens the SnapTrade connection portal in a popup.
// SnapTrade fires CONNECTION_ADDED to our webhook server-side; we poll
// /api/snaptrade/connections until the new row appears, then call onConnected.
export default function SnapTradeConnect({ onConnected, label = 'Connect a brokerage' }) {
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const onClick = useCallback(async () => {
    setErr(null);
    setBusy(true);
    try {
      const before = await apiPost('/api/snaptrade/refresh-connections');
      const beforeIds = new Set((before.connections || []).map((c) => c.id));

      const { redirect_url } = await apiPost('/api/snaptrade/connect-url');
      const popup = window.open(
        redirect_url,
        'snaptrade-connect',
        'width=520,height=720',
      );
      if (!popup) {
        setErr('Popup blocked — allow popups for this site and try again.');
        setBusy(false);
        return;
      }

      // Poll the refresh endpoint every 2s for up to 5 minutes (matches
      // SnapTrade portal expiry). Each poll pulls live auths from SnapTrade
      // and creates rows on our side, so a new row appearing means the user
      // finished the portal flow.
      const startedAt = Date.now();
      const MAX_MS = 5 * 60 * 1000;
      const POLL_MS = 2000;
      let connected = null;
      while (Date.now() - startedAt < MAX_MS) {
        await new Promise((r) => setTimeout(r, POLL_MS));
        try {
          const cur = await apiPost('/api/snaptrade/refresh-connections');
          const fresh = (cur.connections || []).find((c) => !beforeIds.has(c.id));
          if (fresh) {
            connected = fresh;
            break;
          }
        } catch {
          // network blip — keep polling
        }
        if (popup.closed && Date.now() - startedAt > 10_000) {
          // Popup closed but no new row yet — one last reconcile, then bail.
          await new Promise((r) => setTimeout(r, 3_000));
          const cur = await apiPost('/api/snaptrade/refresh-connections').catch(() => null);
          const fresh = cur?.connections?.find((c) => !beforeIds.has(c.id));
          if (fresh) {
            connected = fresh;
          }
          break;
        }
      }

      if (!popup.closed) popup.close();

      if (connected) {
        track('plaid_connected', {
          provider: 'snaptrade',
          institution_name: connected.institution_name,
        });
        onConnected?.(connected);
      } else {
        setErr('We did not detect a completed connection. Try again.');
      }
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }, [onConnected]);

  return (
    <div className="space-y-2">
      <button
        type="button"
        disabled={busy}
        onClick={onClick}
        className="px-5 py-2.5 rounded bg-indigo-500 hover:bg-indigo-400 disabled:opacity-50 text-white font-medium"
      >
        {busy ? 'Waiting for connection…' : label}
      </button>
      {err && <p className="text-sm text-red-400">{err}</p>}
    </div>
  );
}
